// Derived state for the Force Guarantee flow. Kept out of the components so the
// rules live in one place and can be unit tested without rendering.
import { toErrorMessage } from '../../utilities/forceGuarantee';
import {
  PRODUCTS_ERROR_FALLBACK,
  TRANSACTIONS_ERROR_FALLBACK,
  EVALUATION_ERROR_FALLBACK,
  FORCE_REQUEST_ERROR_FALLBACK,
} from '../../constants/forceGuarantee';

// Stable reference so `useSelector` does not see a new array on every dispatch.
const EMPTY_RESULTS = [];

export const selectForceGuarantee = state => state.data.forceGuarantee;

// The views render a ready message instead of the raw error, so the copy stays
// in one place and the backend's own wording is not swallowed by generic text.
export const selectProductsErrorMessage = state => toErrorMessage(
  selectForceGuarantee(state).productsError,
  PRODUCTS_ERROR_FALLBACK
);

export const selectTransactionsErrorMessage = state => toErrorMessage(
  selectForceGuarantee(state).transactionsError,
  TRANSACTIONS_ERROR_FALLBACK
);

export const selectEvaluationErrorMessage = state => toErrorMessage(
  selectForceGuarantee(state).evaluationError,
  EVALUATION_ERROR_FALLBACK
);

export const selectForceErrorMessage = state => toErrorMessage(
  selectForceGuarantee(state).forceError,
  FORCE_REQUEST_ERROR_FALLBACK
);

// Any in-flight request locks every control on the page, so a second request
// can never be started while one is still running.
export const selectIsBusy = state => {
  const {
    productsLoading,
    transactionsLoading,
    evaluationLoading,
    forceLoading,
  } = selectForceGuarantee(state);
  return (
    productsLoading || transactionsLoading || evaluationLoading || forceLoading
  );
};

export const selectEvaluatedId = state => {
  const { evaluation } = selectForceGuarantee(state);
  return evaluation ? evaluation.orderId : null;
};

export const selectEligibilityResults = state => {
  const { evaluation } = selectForceGuarantee(state);
  return evaluation ? evaluation.results : null;
};

// Guard for the force action: the results on screen must belong to the row that
// is currently selected, and that order must not have been forced already.
export const selectCanForce = state => {
  const { evaluation, selectedId, forcedOrderId } = selectForceGuarantee(state);
  return (
    !!evaluation
    && evaluation.orderId === selectedId
    && evaluation.orderId !== forcedOrderId
  );
};

export const selectFailedRulesCount = state => (selectEligibilityResults(state) || EMPTY_RESULTS).filter(
  r => r.outcome === 'fail'
).length;

export const selectTotalRulesCount = state => (selectEligibilityResults(state) || EMPTY_RESULTS).length;

export const selectEvaluatedTransaction = state => {
  const { transactions } = selectForceGuarantee(state);
  const evaluatedId = selectEvaluatedId(state);
  return transactions.find(t => t.id === evaluatedId);
};

export const selectSelectedProduct = state => {
  const { products, search } = selectForceGuarantee(state);
  return products.find(p => p.productCode === search.productCode);
};

// Drives the empty state, so a search returning no orders still gives feedback.
export const selectHasSearched = state => !!selectForceGuarantee(state).search.vin;
